import { useEffect, useState } from 'react';

/**
 * Maximum number of bytes we'll fetch when previewing a CSV file in-browser.
 * Keeps a runaway 100MB CSV from locking the tab; users can still hit the
 * "download" affordance to get the raw file. ~10MiB matches the upper bound
 * used by other in-app previews (e.g. the patient bulk-import flow).
 */
export const CSV_MAX_PREVIEW_BYTES = 10 * 1024 * 1024;

export type CsvFromUrlState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; csvText: string; truncated: boolean }
  | { status: 'error'; error: Error };

export type UseCsvFromUrlOptions = {
  /** Pre-signed/open download URL pointing to the CSV. Falsy disables fetch. */
  url?: string;
  /** Override the max byte budget. Useful for tests. */
  maxBytes?: number;
  /** Custom fetch impl (defaults to global `fetch`). Useful for tests. */
  fetchImpl?: typeof fetch;
};

const truncateText = (text: string, maxBytes: number) => {
  if (typeof TextEncoder === 'undefined') {
    return { text, truncated: false };
  }
  const encoded = new TextEncoder().encode(text);
  if (encoded.byteLength <= maxBytes) {
    return { text, truncated: false };
  }
  const slice = encoded.slice(0, maxBytes);
  // Decode with replacement to avoid emitting a half-character at the cut.
  const decoded = new TextDecoder('utf-8', { fatal: false }).decode(slice);
  return { text: decoded, truncated: true };
};

/**
 * Fetch a CSV file from a (pre-signed) URL and return its body as text. The
 * response is capped at {@link CSV_MAX_PREVIEW_BYTES} so the in-browser viewer
 * stays responsive on huge files; callers can show a "download for full file"
 * affordance when `truncated` is true.
 */
export const useCsvFromUrl = (
  options: UseCsvFromUrlOptions,
): CsvFromUrlState => {
  const { url, maxBytes = CSV_MAX_PREVIEW_BYTES, fetchImpl } = options;
  const [state, setState] = useState<CsvFromUrlState>(
    url ? { status: 'loading' } : { status: 'idle' },
  );

  useEffect(() => {
    if (!url) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    const doFetch = fetchImpl ?? fetch;
    (async () => {
      try {
        const response = await doFetch(url);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch CSV (status ${response.status} ${response.statusText})`,
          );
        }
        const rawText = await response.text();
        const { text, truncated } = truncateText(rawText, maxBytes);
        if (!cancelled) {
          setState({ status: 'success', csvText: text, truncated });
        }
      } catch (e) {
        if (cancelled) return;
        const error = e instanceof Error ? e : new Error(String(e));
        setState({ status: 'error', error });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, maxBytes, fetchImpl]);

  return state;
};
